import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router';
import CastFigure from '../motion/CastFigure';
import { useReducedMotion } from '../motion';
import draftGrades from '../data/draftGrades.json';
import { settlePoseFor } from '../components/grades/RegistrarCountdown';
import { KeeperChip, ValueBar } from '../components/grades/RegistrarBoard';
import { formatRegistrarNumber } from '../components/grades/RegistrarNumber';
import { REGISTRAR_COPY } from '../components/grades/registrarCopy';
import {
  buildContributions,
  buildFiledGradeBands,
  contributionTotal,
  formatMeasurementDek,
} from '../components/grades/dossierScore';
import '../components/grades/registrar.css';

export const DOSSIER_SLUG_ALIASES = Object.freeze({
  aj: 'a-j',
});

export function resolveDossierSlug(slug) {
  if (!slug) return null;
  return DOSSIER_SLUG_ALIASES[slug.toLowerCase()] || null;
}

const GAG_EXHIBITS = Object.freeze({
  'a-j': {
    image: 'aj-tray',
    caption: 'Closing exhibit · Hosting rights did not convey grading immunity.',
  },
  robby: {
    image: 'robby-drawer',
    caption: 'Closing exhibit · The drawer marked NO KEEPER has been entered into evidence.',
  },
  kyle: {
    image: 'kyle-laminate',
    caption: 'Closing exhibit · Lamination preserves the claim, not the ranking.',
  },
  josh: {
    image: 'josh-folder',
    caption: 'Closing exhibit · The Judkins folder remains the clean part of the file.',
  },
  amir: {
    image: 'amir-plaque',
    caption: 'Closing exhibit · The kicker display case remains ready for its first occupant.',
  },
});

const copyFor = (slug) => REGISTRAR_COPY[slug === 'a-j' ? 'aj' : slug];
const isNamedPick = (pick, summary) => pick.player === summary.player && pick.round === summary.round;
const rawUnitFor = (key) => (key === 'draftedEfficiency' ? 'leveraged value' : 'VORP');

function DraftCapitalStrip({ draftCapital }) {
  return (
    <section className="rg-dossier__capital" data-testid="dossier-draft-capital" aria-label="Draft capital, not a grade input">
      <p><strong>Draft capital</strong><span>not a grade input</span></p>
      <b>{draftCapital.phrase}</b>
      <dl>
        {Object.values(draftCapital.phases).map((phase) => (
          <div key={`${phase.firstRound}-${phase.lastRound}`}>
            <dt>R{phase.firstRound}–{phase.lastRound}</dt>
            <dd>{phase.picksHeld}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function DossierHeader({ team, teamCount, reduced }) {
  return (
    <header className="rg-dossier__header" data-testid="dossier-header">
      <div className="rg-dossier__figure-wrap">
        <span className="rg-dossier__rank-ghost" aria-hidden="true">{team.rank}</span>
        <CastFigure
          name={team.manager}
          pose={settlePoseFor(team)}
          trigger="none"
          className="rg-dossier__figure"
          alt={`${team.manager} caricature with grade ${team.letter}`}
        />
      </div>
      <div className="rg-dossier__identity">
        <p className="rg-eyebrow">Official draft dossier · {draftGrades.season}</p>
        <p className="rg-dossier__rank">Rank {team.rank} of {teamCount}</p>
        <h1>{team.team}</h1>
        <p className="rg-dossier__manager">{team.manager}</p>
        <div className="rg-dossier__grade-lockup">
          <strong aria-label={`Grade ${team.letter}`}>{team.letter}</strong>
          <span>{team.score.toFixed(2)} / 100</span>
        </div>
        <DraftCapitalStrip draftCapital={team.draftCapital} />
        {reduced && <span className="rg-dossier__motion-note">Filed without animation</span>}
      </div>
    </header>
  );
}

function DossierCopy({ team }) {
  const copy = copyFor(team.slug);
  return (
    <section className="rg-dossier__copy paper" aria-labelledby="dossier-finding">
      <p className="rg-dossier__section-label">Finding 01 · Clerk's summary</p>
      <h2 id="dossier-finding">{copy.caption}</h2>
      <div className="rg-dossier__roast">
        {copy.roast.split(/\n\n+/).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      </div>
    </section>
  );
}

function CutoffLine({ team }) {
  const bands = buildFiledGradeBands(draftGrades.teams);
  return (
    <figure className="rg-dossier__cutoffs">
      <figcaption>Letter-cutoff line · filed {draftGrades.season} bands</figcaption>
      <div className="rg-dossier__cutoff-track">
        {bands.map((band) => (
          <span
            key={band.letter}
            style={{ left: `${band.low}%`, width: `${band.high - band.low}%` }}
            aria-hidden="true"
          >
            {band.letter}
          </span>
        ))}
        <i style={{ left: `${team.score}%` }} aria-label={`${team.score.toFixed(2)} sits in the filed ${team.letter} band`} />
      </div>
      <p>The marker is this dossier. Boundaries split adjacent letter groups already filed in the source.</p>
    </figure>
  );
}

function ScoreBuild({ team }) {
  const contributions = buildContributions(team, draftGrades.methodology.weights);
  const total = contributionTotal(team, draftGrades.methodology.weights);
  return (
    <section className="rg-dossier__section rg-dossier__score-build" aria-labelledby="score-build-title">
      <p className="rg-dossier__section-label">Finding 02 · Weighted record</p>
      <h2 id="score-build-title">How the score was built</h2>
      <p className="rg-dossier__section-dek">{formatMeasurementDek(contributions.length)}</p>

      <div className="rg-contributions" data-testid="dossier-contributions">
        {contributions.map((component) => (
          <article
            className="rg-contribution"
            key={component.key}
            data-contribution={component.contribution.toFixed(4)}
          >
            <div className="rg-contribution__heading">
              <h3>
                {component.label}
                {team.componentRaw[component.key] !== undefined && (
                  <small>
                    {' · raw '}
                    {formatRegistrarNumber(team.componentRaw[component.key], { precision: 1, signed: true })}
                    {' '}{rawUnitFor(component.key)} · {component.score.toFixed(1)}/100
                  </small>
                )}
              </h3>
              <dl>
                <div><dt>Score</dt><dd>{component.score.toFixed(1)} / 100</dd></div>
                <div><dt>Weight</dt><dd>{Math.round(component.weight * 100)}%</dd></div>
                <div><dt>Contribution</dt><dd>{component.contribution.toFixed(2)} pts</dd></div>
              </dl>
            </div>
            <p>{component.caption}</p>
            <div className="rg-contribution__bar" aria-hidden="true"><i style={{ width: `${component.contribution}%` }} /></div>
          </article>
        ))}
      </div>

      <div className="rg-contribution-total" data-composite={team.score.toFixed(2)}>
        <div className="rg-contribution-total__bar" aria-label={`${team.score.toFixed(2)} total contribution points`}>
          {contributions.map((component) => (
            <i key={component.key} style={{ width: `${component.contribution}%` }} />
          ))}
        </div>
        <p><strong>{total.toFixed(2)} points</strong><span>=</span><strong>{team.score.toFixed(2)} composite</strong></p>
      </div>
      <CutoffLine team={team} />
    </section>
  );
}

function DossierStory({ team }) {
  const copy = copyFor(team.slug);
  return (
    <section
      className="rg-dossier__section rg-dossier__story"
      aria-labelledby="dossier-story-title"
      data-testid="dossier-story"
    >
      <p className="rg-dossier__section-label">THE STORY · FILED BY THE REGISTRAR</p>
      <h2 id="dossier-story-title">The Story</h2>
      <div className="rg-dossier__story-body">
        {copy.story.split(/\n\n+/).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      </div>
    </section>
  );
}

function PickCard({ pick, best, worst }) {
  const sourcesDisagree = pick.sourceSpread > 40;
  return (
    <li
      className={`rg-dossier-pick ${best ? 'is-best' : ''} ${worst ? 'is-worst' : ''}`}
      data-testid="dossier-pick-row"
    >
      <div className="rg-dossier-pick__slot"><span>RD.PK</span><strong>{String(pick.round).padStart(2, '0')}.{String(pick.pick).padStart(2, '0')}</strong></div>
      <div className="rg-dossier-pick__player">
        <span>{best ? 'Best pick' : worst ? 'Worst pick' : `Overall ${pick.overall}`}</span>
        <strong><KeeperChip keeper={pick.keeper} />{pick.player}</strong>
        <span className="rg-dossier-pick__leverage" data-testid="pick-leverage">x{pick.leverageWeight.toFixed(2)} leverage</span>
      </div>
      <div className="rg-dossier-pick__fact"><span>POS</span><strong>{pick.position}</strong></div>
      <div className="rg-dossier-pick__fact"><span>NFL</span><strong>{pick.nflTeam}</strong></div>
      <div className="rg-dossier-pick__fact"><span>BYE</span><strong>{pick.byeWeek}</strong></div>
      <div className="rg-dossier-pick__number"><span>VORP</span><strong>{formatRegistrarNumber(pick.vorp, { precision: 1 })}</strong></div>
      <div className="rg-dossier-pick__value">
        <span>Value vs slot</span>
        <strong>{formatRegistrarNumber(pick.valueVsSlot, { precision: 1, signed: true })}</strong>
        <ValueBar value={pick.valueVsSlot} />
      </div>
      {(pick.availabilityNote || sourcesDisagree) && (
        <p className="rg-dossier-pick__notes">
          {pick.availabilityNote && <span>* {pick.availabilityNote}</span>}
          {sourcesDisagree && <mark>Sources disagree · {pick.sourceSpread.toFixed(1)}-point spread</mark>}
        </p>
      )}
    </li>
  );
}

function PickAppraisals({ team }) {
  const picks = draftGrades.picks
    .filter((pick) => pick.manager === team.manager && !pick.keeper)
    .sort((first, second) => first.overall - second.overall);
  const total = picks.reduce((sum, pick) => sum + pick.valueVsSlot, 0);
  return (
    <section className="rg-dossier__section rg-dossier__picks" aria-labelledby="pick-appraisals-title">
      <p className="rg-dossier__section-label">Finding 04 · {team.draftedPickCount} drafted receipts</p>
      <h2 id="pick-appraisals-title">Every drafted pick, appraised</h2>
      <p className="rg-dossier__section-dek">Keepers are priced separately. Draft order is preserved; gold and red identify the file's best and worst drafted entries.</p>
      <ol className="rg-dossier-picks">
        {picks.map((pick) => (
          <PickCard
            key={pick.overall}
            pick={pick}
            best={isNamedPick(pick, team.bestPick)}
            worst={isNamedPick(pick, team.worstPick)}
          />
        ))}
      </ol>
      <div className="rg-dossier-picks__total">
        <span>Drafted value vs slot · excludes keepers</span>
        <strong>{formatRegistrarNumber(total, { precision: 1, signed: true })}</strong>
      </div>
    </section>
  );
}

function ContextStrip({ team }) {
  return (
    <section className="rg-dossier__context" aria-label="Roster context">
      {team.keeperVerdict.map((keeper) => (
        <article className="is-keeper-price" key={`${keeper.player}-${keeper.keeperCostOverall}`}>
          <span>Priced, not picked</span>
          <strong>{keeper.label}</strong>
          <p>{keeper.player} · round {keeper.round} / overall {keeper.keeperCostOverall} · {formatRegistrarNumber(keeper.keeperSurplus, { precision: 1, signed: true })} surplus</p>
        </article>
      ))}
      {team.byePileups.map((pileup) => (
        <article className="is-warning" key={`${pileup.week}-${pileup.count}`}>
          <span>Bye pileup</span>
          <strong>Week {pileup.week} · {pileup.count} starters</strong>
          <p>{pileup.players.join(' · ')}</p>
        </article>
      ))}
      <article>
        <span>Thinnest-margin risk</span>
        <strong>{team.thinnestMargin.score.toFixed(1)}</strong>
        <p>Bench, bye, and third-quarterback depth risk on a 100-point scale.</p>
      </article>
      <article className="rg-dossier__efficiency">
        <span>Drafted round efficiency · keepers excluded</span>
        <div>
          <p><b>Early</b>{formatRegistrarNumber(team.roundEfficiency.splits.earlyRounds1to5, { precision: 1, signed: true })}</p>
          <p><b>Mid</b>{formatRegistrarNumber(team.roundEfficiency.splits.midRounds6to11, { precision: 1, signed: true })}</p>
          <p><b>Late</b>{formatRegistrarNumber(team.roundEfficiency.splits.lateRounds12to17, { precision: 1, signed: true })}</p>
        </div>
      </article>
    </section>
  );
}

function ClosingExhibit({ team }) {
  const exhibit = GAG_EXHIBITS[team.slug];
  if (!exhibit) return null;
  return (
    <figure className="rg-dossier__closing">
      <img src={`/images/grades/${exhibit.image}.webp`} alt={`${team.manager} closing dossier exhibit`} />
      <figcaption>{exhibit.caption}</figcaption>
    </figure>
  );
}

export default function DraftGradeDossier() {
  const { slug } = useParams();
  const reduced = useReducedMotion();
  const canonicalSlug = resolveDossierSlug(slug);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);

  if (canonicalSlug) {
    return <Navigate to={`/draft-grades/${canonicalSlug}`} replace />;
  }

  const team = draftGrades.teams.find((entry) => entry.slug === slug);
  if (!team) return <Navigate to="/draft-grades" replace />;

  return (
    <main className="rg rg-dossier" data-reduced-motion={reduced ? 'true' : 'false'}>
      <article className="rg-dossier__file" data-tier={team.tier}>
        <DossierHeader team={team} teamCount={draftGrades.teams.length} reduced={reduced} />
        <DossierCopy team={team} />
        <ScoreBuild team={team} />
        <DossierStory team={team} />
        <PickAppraisals team={team} />
        <ContextStrip team={team} />
        <ClosingExhibit team={team} />
        <footer className="rg-dossier__footer">
          <a href="/draft-grades#registrar-ledger">← Back to draft-grade standings</a>
          <a href="/draft-grades#registrar-receipts">Methodology receipts →</a>
        </footer>
      </article>
    </main>
  );
}
